// Fiziksel banka POS (entegre / OPI-ECR) cihaz istemcisi — cihaz-yerel (terazi gibi).
// Kasa PC'sine seri (WebSerial) ile bağlı terminale ödeme tutarını gönderir, onay/red bekler.
// Gerçek cihaz yoksa veya tarayıcı WebSerial desteklemiyorsa SİMÜLASYON moduyla test edilir.
import { useEffect, useState } from "react";

let _port = null;
let _reader = null;
let _writer = null;
const _listeners = new Set();
let _state = { connected: false, error: null };

function _emit() { for (const fn of _listeners) { try { fn(_state); } catch (e) { /* ignore */ } } }
function _set(patch) { _state = { ..._state, ...patch }; _emit(); }
const _delay = (ms) => new Promise((r) => setTimeout(r, ms));
const _rand4 = () => String(Math.floor(1000 + Math.random() * 9000));
const _randCard = () => ["VISA", "MASTERCARD", "TROY"][Math.floor(Math.random() * 3)];

export function bankPosSupported() {
  return typeof navigator !== "undefined" && "serial" in navigator;
}
export function bankPosConnected() { return _state.connected; }

export async function connectBankPos(baudRate = 9600) {
  if (!bankPosSupported()) throw new Error("Bu tarayıcı seri POS bağlantısını desteklemiyor (WebSerial). Chrome/Edge kullanın veya Simülasyon modunda kalın.");
  if (_port) return true;
  const port = await navigator.serial.requestPort();
  await port.open({ baudRate });
  _port = port;
  try { _writer = port.writable.getWriter(); } catch (e) { _writer = null; }
  _set({ connected: true, error: null });
  return true;
}

export async function disconnectBankPos() {
  try { if (_reader) await _reader.cancel(); } catch (e) { /* ignore */ }
  try { if (_writer) { _writer.releaseLock(); } } catch (e) { /* ignore */ }
  try { if (_port) await _port.close(); } catch (e) { /* ignore */ }
  _port = null; _reader = null; _writer = null;
  _set({ connected: false });
}

// OPI/ECR benzeri ASCII istek: tutar kuruş cinsinden. Yanıtta APPROVED/ONAY veya DECLINED/RED aranır.
async function _opiCharge(amount, timeoutMs = 60000) {
  if (!_port || !_writer) throw new Error("POS cihazı bağlı değil");
  const cents = Math.round(amount * 100);
  const req = `SALE;AMOUNT=${cents};CUR=949\r\n`;
  await _writer.write(new TextEncoder().encode(req));
  const decoder = new TextDecoder();
  let buf = "";
  const deadline = Date.now() + timeoutMs;
  _reader = _port.readable.getReader();
  try {
    while (Date.now() < deadline) {
      const { value, done } = await Promise.race([
        _reader.read(),
        _delay(Math.max(0, deadline - Date.now())).then(() => ({ value: undefined, done: true })),
      ]);
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const up = buf.toUpperCase();
      if (/(APPROVED|ONAY|00;|RESULT=0)/.test(up)) {
        const m = buf.match(/REF[=:](\w+)/i);
        return { approved: true, ref: (m && m[1]) || ("POS-" + Date.now().toString().slice(-8)), message: "Onaylandı", cardType: _randCard(), last4: _rand4() };
      }
      if (/(DECLINED|RED|IPTAL|ERROR|RESULT=[1-9])/.test(up)) {
        return { approved: false, ref: null, message: "Kart işlemi reddedildi (cihaz)" };
      }
    }
    return { approved: false, ref: null, message: "POS cihazından yanıt alınamadı (zaman aşımı)" };
  } finally {
    try { _reader.releaseLock(); } catch (e) { /* ignore */ }
    _reader = null;
  }
}

// Kart tutarını cihaza gönderir. config: {enabled, connection, simulate, baud}
// Gerçek çekim yalnız (enabled && connection=serial && bağlı && simulate!=true) iken; aksi halde simülasyon.
export async function chargeBankPos({ amount, config = {} }) {
  const useReal = config.enabled && config.connection === "serial" && config.simulate !== true && bankPosConnected();
  if (!useReal) {
    await _delay(1400);
    return { approved: true, ref: "SIM-" + Date.now().toString().slice(-8), message: "Onaylandı (Simülasyon)", cardType: _randCard(), last4: _rand4(), simulated: true };
  }
  try {
    return await _opiCharge(amount);
  } catch (e) {
    return { approved: false, ref: null, message: e?.message || "POS cihazı hatası" };
  }
}

export function useBankPos() {
  const [state, setState] = useState(_state);
  useEffect(() => {
    const fn = (s) => setState({ ...s });
    _listeners.add(fn);
    fn(_state);
    return () => { _listeners.delete(fn); };
  }, []);
  return state;
}
